import { NavLink } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useBranding } from "../../contexts/BrandingContext";
import {
  Calendar, DollarSign, Users, GraduationCap, BookOpen, Scissors,
  LogOut, ChevronLeft, ChevronRight, Sun, ShoppingCart
} from "lucide-react";
import { useState, useEffect } from "react";
import api from "../../lib/api";

const navItems = [
  { to: "/my-portal", icon: Sun, label: "My Day" },
  { to: "/my-portal/schedule", icon: Calendar, label: "Schedule" },
  { to: "/my-portal/earnings", icon: DollarSign, label: "Earnings" },
  { to: "/my-portal/clients", icon: Users, label: "My Clients" },
  { to: "/my-portal/training", icon: GraduationCap, label: "My Training" },
  // Full Training Studio — same data the owners see (tracks, sessions,
  // evaluations, content library). Gated by access-level training:view.
  { to: "/training", icon: BookOpen, label: "Training Studio" },
  { to: "/my-portal/products", icon: ShoppingCart, label: "Product Goals" },
];

export default function StylistSidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [tenantName, setTenantName] = useState("");
  const { user, logout } = useAuth();
  const branding = useBranding();
  const logoSrc = branding.getLogoSrc();

  useEffect(() => {
    if (!user) { setTenantName(""); return; }
    let cancelled = false;
    api.get("/tenants/me")
      .then(r => { if (!cancelled) setTenantName(r.data?.business_name || ""); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [user]);

  return (
    <aside
      data-testid="stylist-sidebar"
      className={`h-screen sticky top-0 flex flex-col bg-white border-r border-slate-100 shadow-[2px_0_8px_rgba(0,0,0,0.02)] transition-all duration-300 ${
        collapsed ? "w-[72px]" : "w-[220px]"
      }`}
    >
      {/* Logo */}
      <div className="h-auto min-h-16 flex items-center px-4 py-3 border-b border-slate-100">
        {logoSrc ? (
          <img src={logoSrc} alt={branding.app_name} className="w-9 h-9 rounded-xl object-cover flex-shrink-0" />
        ) : (
          <div className="w-9 h-9 rounded-xl gradient-primary flex items-center justify-center flex-shrink-0">
            <Scissors className="w-4 h-4 text-white" strokeWidth={1.5} />
          </div>
        )}
        {!collapsed && (
          <div className="ml-3 min-w-0 flex-1">
            <div className="font-['Outfit'] text-lg font-semibold tracking-tight text-slate-900 truncate">
              {branding.app_name}
            </div>
            {tenantName && tenantName !== branding.app_name && (
              <div
                className="font-['Manrope'] text-[11px] text-slate-400 truncate mt-0.5"
                title={tenantName}
                data-testid="stylist-sidebar-tenant-name">
                {tenantName}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 px-2 space-y-1 overflow-y-auto">
        {navItems.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/my-portal"}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                isActive
                  ? "bg-rose-50 text-rose-600"
                  : "text-slate-500 hover:text-rose-600 hover:bg-rose-50/50"
              }`
            }
          >
            <item.icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.5} />
            {!collapsed && <span className="truncate">{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-slate-100 p-3">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center p-2 rounded-xl text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors mb-2"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
        <div className="flex items-center gap-3 px-2">
          <div className="w-9 h-9 rounded-xl gradient-primary flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 shadow-sm">
            {user?.name?.charAt(0) || "S"}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.title || user?.role}</p>
            </div>
          )}
          {!collapsed && (
            <button onClick={logout} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors" data-testid="stylist-logout-btn">
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}
